/*
 * Trace storefront recorder for Shopify.
 * Captures a privacy-safe DOM replay plus a compact activity timeline.
 * Input values are always masked and sensitive areas can opt out with
 * data-trace-block or data-trace-mask.
 */
(function traceTracker(config) {
  const eventEndpoint = config && config.endpoint ? config.endpoint : '/api/events';
  const replayEndpoint = config && config.replayEndpoint
    ? config.replayEndpoint
    : eventEndpoint.replace(/\/events(?:\?.*)?$/, '/replay');
  const consentRequired = config && config.consentRequired !== false;
  const sessionKey = 'trace_session_id';
  const visitorKey = 'trace_visitor_id';
  const startedAt = Date.now();
  const randomId = globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
    ? globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    : Math.random().toString(36).slice(2, 18);
  const sessionId = sessionStorage.getItem(sessionKey) || `sess-${randomId}`;
  sessionStorage.setItem(sessionKey, sessionId);
  const visitorId = localStorage.getItem(visitorKey) || `visitor-${randomId}`;
  localStorage.setItem(visitorKey, visitorId);

  let started = false;
  let recording = false;
  let pendingCustomer = null;
  let idle = false;
  let idleTimer = null;
  let replayTimer = null;
  let heartbeatTimer = null;
  let replayEvents = [];
  let pendingRequests = [];
  let retryTimer = null;
  let eventSequence = 0;
  let cartSnapshot = { cartValue: 0, cartCount: 0 };
  const semanticSentAt = new Map();

  function allowed() {
    if (!consentRequired || window.__TRACE_CONSENT__ === true) return true;
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return true;
    if (typeof privacy.analyticsProcessingAllowed === 'function') return privacy.analyticsProcessingAllowed() === true;
    if (typeof privacy.userCanBeTracked === 'function') return privacy.userCanBeTracked() === true;
    return false;
  }

  function basePayload() {
    return {
      sessionId,
      visitorId,
      path: location.pathname + location.search,
      pageTitle: document.title,
      referrer: document.referrer || '',
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      device: navigator.userAgent || '',
      startedAt
    };
  }

  function scheduleRetry() {
    if (retryTimer || !pendingRequests.length) return;
    retryTimer = setTimeout(async () => {
      retryTimer = null;
      const queue = pendingRequests.splice(0, 8);
      for (const item of queue) {
        try {
          const response = await fetch(item.url, {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            body: item.body,
            keepalive: item.body.length < 60000
          });
          if (!response.ok) throw new Error(`Trace returned ${response.status}`);
        } catch {
          if (item.attempt < 4) pendingRequests.push({ ...item, attempt: item.attempt + 1 });
        }
      }
      if (pendingRequests.length) scheduleRetry();
    }, 2200);
  }

  async function postJson(url, payload) {
    const body = JSON.stringify(payload);
    try {
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: body.length < 60000
      });
      if (!response.ok) throw new Error(`Trace returned ${response.status}`);
      return true;
    } catch {
      pendingRequests.push({ url, body, attempt: 1 });
      scheduleRetry();
      return false;
    }
  }

  function send(type, detail, extra) {
    if (!allowed()) return;
    const payload = {
      ...basePayload(),
      eventId: `${sessionId}-${Date.now()}-${++eventSequence}`,
      type,
      detail: detail || '',
      ...(extra || {})
    };
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon && body.length < 60000 && ['click', 'session_end', 'visibility_hidden'].includes(type)) {
      navigator.sendBeacon(eventEndpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
    }
    postJson(eventEndpoint, payload);
  }

  function heartbeat() {
    if (!started || document.hidden || !allowed()) return;
    send('heartbeat', 'Presence confirmed', { idle });
  }

  function semanticAllowed(type, label) {
    const key = `${type}:${label || ''}`;
    const now = Date.now();
    if (now - Number(semanticSentAt.get(key) || 0) < 1800) return false;
    semanticSentAt.set(key, now);
    return true;
  }

  async function refreshCartSnapshot() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (response.ok) {
        const cart = await response.json();
        cartSnapshot = {
          cartValue: Number(cart.total_price || 0) / 100,
          cartCount: Number(cart.item_count || 0)
        };
      }
    } catch {
      // Keep the last known snapshot when Shopify's cart endpoint is unavailable.
    }
    return cartSnapshot;
  }

  async function sendCartEvent(type, label) {
    if (!semanticAllowed(type, label)) return;
    const snapshot = await refreshCartSnapshot();
    send(type, label, snapshot);
  }

  function cartAddLabel(payload) {
    const item = payload && Array.isArray(payload.items) ? payload.items[0] : payload;
    return item && (item.product_title || item.title || item.handle)
      ? String(item.product_title || item.title || item.handle).slice(0, 120)
      : (document.querySelector('.product-single__title')?.textContent?.trim() || document.title || 'Product');
  }

  function reportSuccessfulCartAdd(payload) {
    const label = cartAddLabel(payload);
    sendCartEvent('add_to_cart', label);
    window.dispatchEvent(new CustomEvent('velvet:cart-added', { detail: { label, payload } }));
  }

  function installCartAddObserver() {
    if (window.__VELVET_CART_ADD_OBSERVER__) return;
    window.__VELVET_CART_ADD_OBSERVER__ = true;

    const nativeFetch = window.fetch.bind(window);
    window.fetch = async function traceFetch(input, init) {
      const response = await nativeFetch(input, init);
      try {
        const url = new URL(typeof input === 'string' ? input : input.url, location.origin);
        const method = String(init?.method || (typeof input !== 'string' && input.method) || 'GET').toUpperCase();
        if (method === 'POST' && /\/cart\/add\.js$/.test(url.pathname) && response.ok) {
          response.clone().json().then(reportSuccessfulCartAdd).catch(() => reportSuccessfulCartAdd(null));
        }
      } catch {
        // Never interfere with the original storefront request.
      }
      return response;
    };

    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function traceOpen(method, url) {
      this.__traceCartAdd = String(method || 'GET').toUpperCase() === 'POST' && /\/cart\/add\.js(?:\?|$)/.test(String(url || ''));
      return nativeOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function traceSend() {
      if (this.__traceCartAdd) {
        this.addEventListener('load', () => {
          if (this.status < 200 || this.status >= 300) return;
          try { reportSuccessfulCartAdd(JSON.parse(this.responseText || 'null')); }
          catch { reportSuccessfulCartAdd(null); }
        }, { once: true });
      }
      return nativeSend.apply(this, arguments);
    };
  }

  function sendCheckoutEvent(label) {
    const detail = label || 'Checkout started';
    if (!semanticAllowed('checkout_start', detail)) return;
    send('checkout_start', detail, cartSnapshot);
  }

  function flushReplay(useBeacon) {
    clearTimeout(replayTimer);
    replayTimer = null;
    if (!replayEvents.length || !allowed()) return;
    const events = replayEvents;
    replayEvents = [];
    const payload = { ...basePayload(), events };
    const body = JSON.stringify(payload);
    if (useBeacon && navigator.sendBeacon && body.length < 60000) {
      const accepted = navigator.sendBeacon(replayEndpoint, new Blob([body], { type: 'application/json' }));
      if (accepted) return;
    }
    postJson(replayEndpoint, payload);
  }

  function queueReplay(event) {
    replayEvents.push(event);
    if (replayEvents.length >= 20) flushReplay(false);
    else if (!replayTimer) replayTimer = setTimeout(() => flushReplay(false), 1200);
  }

  function startReplay() {
    if (recording || !allowed()) return;
    const recorder = window.rrwebRecord && window.rrwebRecord.record;
    if (typeof recorder !== 'function') {
      send('recorder_error', 'Replay recorder did not load');
      return;
    }
    recording = true;
    recorder({
      emit: queueReplay,
      checkoutEveryNms: 60000,
      maskAllInputs: true,
      maskTextSelector: '[data-trace-mask], [data-private], [contenteditable="true"]',
      blockSelector: '[data-trace-block], iframe, video, audio',
      recordCanvas: false,
      collectFonts: false,
      inlineImages: false,
      sampling: {
        mousemove: 80,
        touchmove: 80,
        scroll: 100,
        input: 'last'
      },
      slimDOMOptions: {
        script: true,
        comment: true,
        headFavicon: true,
        headWhitespace: true
      }
    });
  }

  function noteActivity() {
    if (!started) return;
    if (idle) {
      idle = false;
      send('activity_resume', 'Visitor resumed activity');
    }
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      idle = true;
      send('idle_start', '30 sec idle');
    }, 30000);
  }

  function boot() {
    if (started || !allowed()) return;
    started = true;
    send('session_start', 'New session');
    send('page_view', location.pathname + location.search);
    refreshCartSnapshot();
    if (pendingCustomer && pendingCustomer.id) {
      send('identify', 'Customer identified', {
        customerId: pendingCustomer.id,
        customerName: pendingCustomer.name,
        customerEmail: pendingCustomer.email,
        identitySource: pendingCustomer.source
      });
    } else if (pendingCustomer && pendingCustomer.email) {
      send('identify', 'Newsletter subscriber identified', {
        customerName: pendingCustomer.name,
        customerEmail: pendingCustomer.email,
        identitySource: pendingCustomer.source || 'newsletter'
      });
    }

    ['mousemove', 'scroll', 'keydown', 'touchstart'].forEach((eventName) => {
      window.addEventListener(eventName, noteActivity, { passive: true });
    });
    document.addEventListener('click', (event) => {
      noteActivity();
      const target = event.target && event.target.closest
        ? event.target.closest('[data-trace-label], a, button, input[type="submit"]')
        : null;
      if (!target) return;
      const label = target.dataset.traceLabel
        || target.getAttribute('aria-label')
        || target.textContent.trim().slice(0, 80)
        || target.tagName;
      send('click', label, {
        selector: target.tagName.toLowerCase(),
        x: Math.round(event.clientX),
        y: Math.round(event.clientY),
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
      });
      const checkoutTarget = target.matches('[name="checkout"], [href*="/checkout"]')
        || target.closest('[name="checkout"], [href*="/checkout"]');
      if (checkoutTarget) sendCheckoutEvent('Checkout started');
    }, { passive: true });

    document.addEventListener('submit', (event) => {
      const form = event.target && event.target.closest ? event.target.closest('form') : null;
      if (!form) return;
      const action = String(form.getAttribute('action') || '');
      if (form.querySelector('[name="checkout"]')) {
        sendCheckoutEvent('Checkout started');
      }
      const emailInput = form.querySelector('input[type="email"], input[name*="email" i]');
      const marker = [form.id, form.className, action, form.querySelector('[name="contact[tags]"]')?.value]
        .filter(Boolean).join(' ').toLowerCase();
      const email = String(emailInput?.value || '').trim().toLowerCase();
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && /(newsletter|subscribe|marketing)/.test(marker)) {
        const firstName = String(form.querySelector('input[name*="first_name" i]')?.value || '').trim();
        const lastName = String(form.querySelector('input[name*="last_name" i]')?.value || '').trim();
        const name = [firstName, lastName].filter(Boolean).join(' ');
        pendingCustomer = { id: '', name, email, source: 'newsletter' };
        send('identify', 'Newsletter subscriber identified', {
          customerName: name,
          customerEmail: email,
          identitySource: 'newsletter'
        });
      }
    }, { passive: true });

    let lastScrollDepth = 0;
    window.addEventListener('scroll', () => {
      const depth = Math.min(100, Math.round((window.scrollY / Math.max(1, document.documentElement.scrollHeight - window.innerHeight)) * 100));
      if (depth >= lastScrollDepth + 25) {
        lastScrollDepth = depth;
        send('scroll', `${depth}% depth`, { depth, viewportWidth: window.innerWidth, viewportHeight: window.innerHeight });
      }
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      send(document.hidden ? 'visibility_hidden' : 'visibility_visible', document.hidden ? 'Tab moved to background' : 'Tab visible again');
      if (!document.hidden) {
        noteActivity();
        heartbeat();
      }
    });
    window.addEventListener('pagehide', () => {
      clearInterval(heartbeatTimer);
      send('session_end', 'Page closed or navigated');
      flushReplay(true);
    });

    noteActivity();
    heartbeat();
    heartbeatTimer = setInterval(heartbeat, 10000);
    startReplay();
  }

  function startWhenAllowed() {
    if (allowed()) boot();
  }

  window.Trace = {
    boot,
    status() {
      return { sessionId, visitorId, started, recording, consentAllowed: allowed(), queued: pendingRequests.length, replayQueued: replayEvents.length };
    },
    identify(customer) {
      const identity = typeof customer === 'object' ? customer : { id: customer };
      pendingCustomer = {
        id: String(identity.id || ''),
        name: String(identity.name || '').slice(0, 120),
        email: String(identity.email || '').slice(0, 180),
        source: String(identity.source || (identity.id ? 'shopify_account' : 'newsletter')).slice(0, 40)
      };
      if (started && (pendingCustomer.id || pendingCustomer.email)) {
        send('identify', 'Customer identified', {
          customerId: pendingCustomer.id,
          customerName: pendingCustomer.name,
          customerEmail: pendingCustomer.email,
          identitySource: pendingCustomer.source
        });
      }
    },
    trackCartAdd(payload) {
      reportSuccessfulCartAdd(payload || null);
    }
  };

  installCartAddObserver();

  document.addEventListener('visitorConsentCollected', startWhenAllowed);
  document.addEventListener('trackingConsentAccepted', startWhenAllowed);
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], startWhenAllowed);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWhenAllowed, { once: true });
  } else {
    startWhenAllowed();
  }
})(window.TRACE_CONFIG || {});
